import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import type { ProblemDetailsWithCode } from "@/lib/problemDetails";
import { ConfirmActionDialog } from "@/components/ConfirmActionDialog";
import { api, setActiveClientId } from "@/api/client";
import type { components } from "@dolfin/api-admin";
import { InfoHint } from "@dolfin/ui";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";
import { ExternalLinkIcon, ActivityIcon, BuildingIcon, Building2Icon, KeyIcon, InfoIcon, PencilIcon, UsersIcon, WebhookIcon, BarChart3Icon, GitCompareIcon, SlidersHorizontalIcon, EyeOffIcon, FlaskConicalIcon, NetworkIcon, Trash2Icon } from "lucide-react";
import { ActivityView } from "@/components/activity/ActivityView";
import { ResourceTotalsGrid } from "@/components/activity/ResourceTotalsGrid";
import { MatchStatsPanel } from "@/components/MatchStatsPanel";
import { MatchLinesTable } from "@/components/MatchLinesTable";
import { EInvoicingRegistrationsTable } from "@/components/EInvoicingRegistrationsTable";
import { MatchGroupsTable } from "@/components/MatchGroupsTable";
import { ApiKeysTable } from "@/components/ApiKeysTable";
import { ClientMembersTable } from "@/components/ClientMembersTable";
import { ClientOrganisationsTable } from "@/components/ClientOrganisationsTable";
import { ClientCompaniesTable } from "@/components/ClientCompaniesTable";
import { EditClientDialog } from "@/components/EditClientDialog";
import { ClientModulesCard } from "@/components/ClientModulesCard";
import { WhatsAppConnectionCard } from "@/components/WhatsAppConnectionCard";
import { WebhookSubscriptionsTable } from "@/components/WebhookSubscriptionsTable";
import { ApSettingsSection } from "@/components/ApSettingsSection";
import { MatchTestingSection } from "@/components/MatchTestingSection";
import { ArSettingsSection } from "@/components/ArSettingsSection";
import { HiddenClientSettingsSection } from "@/components/HiddenClientSettingsSection";
import { Button } from "@/components/ui/button";
import { formatDateTime } from "@/lib/format";
import { cn } from "@/lib/utils";
import { useRecordNav } from "@/components/RecordNav";

function formatPercent(value: number | string | undefined): string {
  if (value === undefined || value === null) return "—";
  const num = typeof value === "string" ? parseFloat(value) : value;
  if (Number.isNaN(num)) return "—";
  return `${num.toFixed(2)}%`;
}

type Client = components["schemas"]["ClientAdminResponse"];

type Section =
  | "info"
  | "stats"
  | "analytics"
  | "match-stats"
  | "organisations"
  | "companies"
  | "members"
  | "api-keys"
  | "match-groups"
  | "match-testing"
  | "webhooks"
  | "e-invoicing"
  | "ap-settings"
  | "ar-settings"
  | "hidden-settings";

const navItems: { id: Section; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
  { id: "info", label: "Info", icon: InfoIcon },
  { id: "members", label: "Members", icon: UsersIcon },
  { id: "organisations", label: "Organisations", icon: BuildingIcon },
  { id: "companies", label: "Companies", icon: Building2Icon },
  { id: "stats", label: "Stats", icon: BarChart3Icon },
  { id: "analytics", label: "Analytics", icon: ActivityIcon },
  { id: "match-stats", label: "Match Stats", icon: GitCompareIcon },
  { id: "match-groups", label: "Match Groups", icon: GitCompareIcon },
  { id: "match-testing", label: "Match Testing", icon: FlaskConicalIcon },
  { id: "api-keys", label: "API Keys", icon: KeyIcon },
  { id: "webhooks", label: "Webhooks", icon: WebhookIcon },
  { id: "e-invoicing", label: "E-Invoicing", icon: NetworkIcon },
  { id: "ap-settings", label: "AP Settings", icon: SlidersHorizontalIcon },
  { id: "ar-settings", label: "AR Settings", icon: SlidersHorizontalIcon },
  { id: "hidden-settings", label: "Hidden Settings", icon: EyeOffIcon },
];

const sectionIds = new Set<Section>(navItems.map((item) => item.id));

function readSectionFromHash(): Section {
  const raw = window.location.hash.replace(/^#/, "");
  // Old links to the tab before it became Analytics.
  const hash = (raw === "active-orgs" ? "analytics" : raw) as Section;
  return sectionIds.has(hash) ? hash : "info";
}

export function ClientPage() {
  const { clientId } = useParams<{ clientId: string }>();

  const [section, setSectionState] = useState<Section>(readSectionFromHash);
  const [client, setClient] = useState<Client | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!clientId) return;
    setActiveClientId(clientId);
    return () => setActiveClientId(null);
  }, [clientId]);

  useEffect(() => {
    function onHashChange() {
      setSectionState(readSectionFromHash());
    }
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);


  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;

    async function fetchClient() {
      setLoading(true);
      const clientRes = await api.GET("/admin/clients/{id}", { params: { path: { id: clientId! } } });
      if (cancelled) return;
      if (clientRes.error) {
        setError("Failed to load client.");
      } else {
        setClient(clientRes.data ?? null);
      }
      setLoading(false);
    }

    fetchClient();
    return () => {
      cancelled = true;
    };
  }, [clientId]);

  useRecordNav({
    back: { to: "/clients", label: "Clients" },
    kind: "Client",
    title: client?.name ?? "Client",
    items: navItems,
    active: section,
  });

  if (error) {
    return (
      <div className="mx-auto w-full max-w-[100rem] p-8">
        <p className="text-destructive">{error}</p>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-[100rem] p-8">
      <div className="mb-6">
        <div className="flex items-center gap-2">
          <h1 className="text-2xl font-bold">{client?.name ?? "Client"}</h1>
          {client?.type && (
            <Badge variant={client.type === "Live" ? "default" : "outline"}>
              {client.type}
            </Badge>
          )}
        </div>
        {client && (
          <div className="mt-1">
            <Badge variant="secondary" className="font-mono text-xs">
              {client.id}
            </Badge>
          </div>
        )}
      </div>

      <div className="flex gap-6">

        <div className="min-w-0 flex-1">
          {section === "info" && (
            <div className="space-y-6">
              <ClientInfoCard
                client={client}
                loading={loading}
                // PUT /clients/{id} returns ClientResponse, without sourceClientId/clonedAt: merge so clone provenance survives.
                onUpdated={(updated) => setClient((prev) => (prev ? { ...prev, ...updated } : updated))}
              />
              {clientId && <ClientModulesCard clientId={clientId} />}
              {clientId && <WhatsAppConnectionCard clientId={clientId} />}
            </div>
          )}

          {section === "stats" && clientId && (
            <section>
              <h2 className="mb-4 text-lg font-semibold">Stats</h2>
              <ClientStatsCard clientId={clientId} />
            </section>
          )}

          {section === "analytics" && clientId && (
            <section>
              <h2 className="mb-1 text-lg font-semibold">Analytics</h2>
              <p className="text-muted-foreground mb-4 text-sm">
                What this client is billed on: the organisations that created something in each month.
              </p>
              <ActivityView clientId={clientId} showTotals={false} />
            </section>
          )}

          {section === "match-stats" && clientId && (
            <section>
              <h2 className="mb-4 text-lg font-semibold">Match Stats</h2>
              <MatchStatsPanel clientId={clientId} />

              <h3 className="mb-3 mt-8 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                Lines
              </h3>
              <MatchLinesTable clientId={clientId} />
            </section>
          )}

          {section === "match-groups" && clientId && (
            <section>
              <h2 className="mb-4 text-lg font-semibold">Match Groups</h2>
              <p className="text-muted-foreground mb-4 text-sm">
                Every three-way match across this client's organisations. Open one to see what the
                organisation's own reviewer sees.
              </p>
              <MatchGroupsTable clientId={clientId} />
            </section>
          )}

          {section === "e-invoicing" && clientId && (
            <section>
              <h2 className="mb-4 text-lg font-semibold">E-Invoicing Registrations</h2>
              <p className="text-muted-foreground mb-4 text-sm">
                Each of this client&apos;s organisations that has tried to become a network
                participant. Registration is a multi-day conversation with an access point, so a row
                sitting in Submitted is normal for a while and a worry after that — open one to see
                which workflow step the provider is on, or which one refused it.
              </p>
              <EInvoicingRegistrationsTable clientId={clientId} />
            </section>
          )}

          {section === "organisations" && clientId && (
            <section>
              <h2 className="mb-4 text-lg font-semibold">Organisations</h2>
              <ClientOrganisationsTable clientId={clientId} />
            </section>
          )}

          {section === "companies" && clientId && (
            <section>
              <h2 className="mb-4 text-lg font-semibold">Companies</h2>
              <p className="text-muted-foreground mb-4 text-sm">
                Parent groups above this client's organisations. A bill addressed to the group arrives
                in the company's inbox and is routed to one of its sites.
              </p>
              <ClientCompaniesTable clientId={clientId} />
            </section>
          )}

          {section === "members" && clientId && (
            <section>
              <h2 className="mb-4 text-lg font-semibold">Members</h2>
              <ClientMembersTable clientId={clientId} />
            </section>
          )}

          {section === "api-keys" && clientId && (
            <section>
              <h2 className="mb-4 text-lg font-semibold">API Keys</h2>
              <ApiKeysTable clientId={clientId} />
            </section>
          )}

          {section === "webhooks" && clientId && (
            <section>
              <h2 className="mb-4 text-lg font-semibold">Webhooks</h2>
              <WebhookSubscriptionsTable clientId={clientId} />
            </section>
          )}

          {section === "ap-settings" && clientId && (
            <section>
              <ApSettingsSection clientId={clientId} />
            </section>
          )}

          {section === "match-testing" && clientId && client && (
            <section>
              <MatchTestingSection clientId={clientId} client={client} />
            </section>
          )}

          {section === "ar-settings" && clientId && (
            <section>
              <ArSettingsSection clientId={clientId} />
            </section>
          )}

          {section === "hidden-settings" && clientId && (
            <section>
              <HiddenClientSettingsSection clientId={clientId} />
            </section>
          )}
        </div>
      </div>
    </div>
  );
}

function ClientInfoCard({
  client,
  loading,
  onUpdated,
}: {
  client: Client | null;
  loading: boolean;
  onUpdated: (client: Client) => void;
}) {
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const navigate = useNavigate();

  async function handleDelete() {
    if (!client?.id) return;
    setDeleting(true);
    const { error: apiError } = await api.DELETE("/admin/clients/{id}", {
      params: { path: { id: client.id } },
    });
    setDeleting(false);

    if (apiError) {
      // Every failure is refused before anything is deleted, so the clone is intact; keep the backend's `detail`.
      const problem = apiError as ProblemDetailsWithCode | undefined;
      const detail = problem?.detail;
      let message: string;
      if (problem?.code === "Clone.NotAClone") {
        message = detail || "This client isn't a match-testing clone, so it can't be deleted.";
      } else if (problem?.code === "Clone.DeleteBlocked") {
        message = detail || "Something still references this clone's data, so nothing was deleted.";
      } else if (problem?.code === "Client.NotFound") {
        message = detail || "This client could not be found. It may already have been deleted.";
      } else {
        message = detail || "Failed to delete the clone.";
      }
      toast.error("Delete failed", { description: message });
      return;
    }

    setDeleteOpen(false);
    toast.success("Clone deleted", { description: client.name });
    navigate("/clients");
  }

  if (loading) {
    return (
      <div className="flex justify-center py-4">
        <Spinner className="size-5" />
      </div>
    );
  }
  if (!client) {
    return <p className="text-muted-foreground">Client not found.</p>;
  }

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4">
          <CardTitle>{client.name}</CardTitle>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => setEditOpen(true)}>
              <PencilIcon className="size-4" />
              Edit
            </Button>
            {/* Clones only: same gate as DeleteClientAdminEndpoint. */}
            {client.sourceClientId && (
              <Button size="sm" variant="destructive" onClick={() => setDeleteOpen(true)}>
                <Trash2Icon className="size-4" />
                Delete clone
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <dl className="grid [&>div]:min-w-0 grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <dt className="text-muted-foreground text-sm">Client ID</dt>
              <dd className="mt-1">
                <Badge variant="secondary" className="font-mono text-xs">
                  {client.id}
                </Badge>
              </dd>
            </div>
            <div>
              <dt className="text-muted-foreground text-sm">Type</dt>
              <dd className="mt-1">
                {client.type ? (
                  <Badge variant={client.type === "Live" ? "default" : "outline"}>
                    {client.type}
                  </Badge>
                ) : (
                  <span className="text-sm text-muted-foreground">—</span>
                )}
              </dd>
            </div>
            {client.sourceClientId && (
              <div>
                <dt className="text-muted-foreground flex items-center gap-1 text-sm">
                  Cloned from
                  <InfoHint label="Cloned from">
                    This client was created as a match-testing clone of another client. The source may
                    since have been deleted, in which case the link below 404s.
                  </InfoHint>
                </dt>
                <dd className="mt-1 flex items-center gap-2">
                  <Link to={`/clients/${client.sourceClientId}`}>
                    <Badge variant="secondary" className="font-mono text-xs">
                      {client.sourceClientId.slice(0, 8)}...
                    </Badge>
                  </Link>
                  {client.clonedAt && (
                    <span className="text-muted-foreground text-xs">
                      {formatDateTime(client.clonedAt)}
                    </span>
                  )}
                </dd>
              </div>
            )}
            <div>
              <dt className="text-muted-foreground text-sm">Dolfin rev share</dt>
              <dd className="mt-1 text-sm">{formatPercent(client.dolfinRevenueSharePercent)}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground text-sm">Client rev share</dt>
              <dd className="mt-1 text-sm">{formatPercent(client.paymentRevenueSharePercent)}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground text-sm">Logo</dt>
              <dd className="mt-1 text-sm">
                {client.logoUrl ? (
                  <a
                    href={client.logoUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 text-primary hover:underline"
                  >
                    <img
                      src={client.logoUrl}
                      alt={`${client.name} logo`}
                      className="size-8 rounded border object-contain"
                    />
                    <ExternalLinkIcon className="size-3" />
                  </a>
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </dd>
            </div>
            <div>
              <dt className="text-muted-foreground text-sm">Primary colour</dt>
              <dd className="mt-1 text-sm">
                {client.primaryColour ? (
                  <span className="inline-flex items-center gap-2">
                    <span
                      className="size-4 rounded border"
                      style={{ backgroundColor: client.primaryColour }}
                    />
                    <span className="font-mono text-xs">{client.primaryColour}</span>
                  </span>
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </dd>
            </div>
            <div>
              <dt className="text-muted-foreground text-sm">Created</dt>
              <dd className="mt-1 text-sm">{formatDateTime(client.createdAt)}</dd>
              {client.createdBy && (
                <dd className="mt-1">
                  <Link
                    to={`/users/${client.createdBy}`}
                    className="inline-flex items-center gap-1 text-primary hover:underline"
                  >
                    <Badge variant="secondary" className="font-mono text-xs">
                      {client.createdBy.slice(0, 8)}...
                    </Badge>
                    <ExternalLinkIcon className="size-3" />
                  </Link>
                </dd>
              )}
            </div>
            <div>
              <dt className="text-muted-foreground text-sm">Updated</dt>
              <dd className="mt-1 text-sm">{formatDateTime(client.updatedAt)}</dd>
              {client.updatedBy && (
                <dd className="mt-1">
                  <Link
                    to={`/users/${client.updatedBy}`}
                    className="inline-flex items-center gap-1 text-primary hover:underline"
                  >
                    <Badge variant="secondary" className="font-mono text-xs">
                      {client.updatedBy.slice(0, 8)}...
                    </Badge>
                    <ExternalLinkIcon className="size-3" />
                  </Link>
                </dd>
              )}
            </div>
          </dl>
        </CardContent>
      </Card>

      <EditClientDialog
        client={client}
        open={editOpen}
        onOpenChange={setEditOpen}
        onUpdated={onUpdated}
      />

      {client.sourceClientId && (
        <ConfirmActionDialog
          open={deleteOpen}
          onOpenChange={setDeleteOpen}
          title="Delete this clone?"
          description={
            <>
              Permanently deletes <span className="font-medium">{client.name}</span> and everything it
              holds: its organisations, suppliers, bills, purchase orders, delivery notes, match data and
              settings. The client it was cloned from is not touched. There is no undo.
            </>
          }
          confirmLabel="Delete clone"
          pending={deleting}
          onConfirm={handleDelete}
        />
      )}
    </>
  );
}

type ClientStats = components["schemas"]["ClientStatsResponse"];

function toNumber(value: number | string | undefined): number | null {
  if (value === undefined || value === null) return null;
  const num = typeof value === "string" ? parseFloat(value) : value;
  return Number.isNaN(num) ? null : num;
}

function ClientStatsCard({ clientId }: { clientId: string }) {
  const [stats, setStats] = useState<ClientStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function fetchStats() {
      setLoading(true);
      setError(null);
      const { data, error: apiError } = await api.GET("/v1/client/stats");
      if (cancelled) return;
      if (apiError) {
        setError("Failed to load stats.");
      } else {
        setStats(data ?? null);
      }
      setLoading(false);
    }
    fetchStats();
    return () => {
      cancelled = true;
    };
  }, [clientId]);

  if (loading) {
    return (
      <div className="flex justify-center py-4">
        <Spinner className="size-5" />
      </div>
    );
  }
  if (error) {
    return <p className="text-destructive text-sm">{error}</p>;
  }
  if (!stats) {
    return <p className="text-muted-foreground">No stats available.</p>;
  }

  const accounts: { label: string; value: number | null }[] = [
    { label: "Organisations", value: toNumber(stats.organisations) },
    { label: "Users", value: toNumber(stats.users) },
    { label: "Portal members", value: toNumber(stats.portalMembers) },
    { label: "API keys", value: toNumber(stats.apiKeys) },
    { label: "Webhook subscriptions", value: toNumber(stats.webhookSubscriptions) },
  ];
  const records = {
    Bills: { count: toNumber(stats.bills) ?? 0 },
    Invoices: { count: toNumber(stats.invoices) ?? 0 },
    Customers: { count: toNumber(stats.customers) ?? 0 },
    Suppliers: { count: toNumber(stats.suppliers) ?? 0 },
    Products: { count: toNumber(stats.products) ?? 0 },
    TaxRates: { count: toNumber(stats.taxRates) ?? 0 },
    PurchaseOrders: { count: toNumber(stats.purchaseOrders) ?? 0 },
    DeliveryNotes: { count: toNumber(stats.deliveryNotes) ?? 0 },
    CreditNotes: { count: toNumber(stats.creditNotes) ?? 0 },
    SupplierCreditNotes: { count: toNumber(stats.supplierCreditNotes) ?? 0 },
    Expenses: { count: toNumber(stats.expenses) ?? 0 },
    Receipts: { count: toNumber(stats.receipts) ?? 0 },
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Accounts</CardTitle>
          <p className="text-muted-foreground text-xs">
            As of {formatDateTime(stats.asOf)} · values can lag real state by up to an hour.
          </p>
        </CardHeader>
        <CardContent>
          <dl className="grid [&>div]:min-w-0 grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
            {accounts.map((tile) => (
              <div key={tile.label} className="rounded-md border px-3 py-2.5">
                <dt className="text-muted-foreground truncate text-xs">{tile.label}</dt>
                <dd className="mt-0.5 text-xl font-semibold tabular-nums">
                  {tile.value === null ? "-" : tile.value.toLocaleString("en-GB")}
                </dd>
              </div>
            ))}
          </dl>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Records</CardTitle>
          <p className="text-muted-foreground text-xs">
            Everything created to date across this client's organisations. The Analytics tab breaks it down by
            month, week and organisation.
          </p>
        </CardHeader>
        <CardContent>
          <ResourceTotalsGrid totals={records} />
        </CardContent>
      </Card>
    </div>
  );
}
